import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import CardSelectors from 'Stores/Card/Selectors'
import CardActions from 'Stores/Card/Actions'
import RequestState from 'Enums/RequestState'
import Styles from './ChargingTokenTransactionList.module.css'
import { Waypoint } from 'react-waypoint'
import { TransactionListItem, TransactionListItemSkeleton } from 'Components'
import { EmptyLogs } from 'Components'
import { NoLogsFilter } from 'Components'
import InfinityEnums from 'Enums/InfinityEnums'
import { Virtuoso } from 'react-virtuoso'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import 'overlayscrollbars/overlayscrollbars.css'
const { ConnectorPowerLevel } = InfinityEnums

const ChargingTokenTransactionList = ({
  fetchChargingTokenTransactions,
  fetchChargingTokenTransactionsRequestState,
  chargingTokenTransactions,
  paginationOffset,
  cardTransactionsFilter,
  chargingTokens,
  selectedChargingTokenIndex,
  fetchChargingTokensRequestState,
  ascending,
  selectedCard,
  fetchCardRequestState,
}) => {
  const [empty, setEmpty] = useState(true)

  const Footer = () => {
    return paginationOffset &&
      chargingTokenTransactions &&
      chargingTokenTransactions.length > 0 ? (
      <div className={Styles.WaypointContainer}>
        <Waypoint onEnter={loadMoreData} />
        <TransactionListItemSkeleton />
        <TransactionListItemSkeleton />
      </div>
    ) : null
  }

  const loadMoreData = () => {
    if (
      fetchChargingTokenTransactionsRequestState === RequestState.LOADING_MORE
    )
      return
    fetchChargingTokenTransactions(
      cardTransactionsFilter,
      paginationOffset,
      chargingTokens[selectedChargingTokenIndex]?._id,
      ascending,
    )
  }

  const handleMaxPower = (maxPower) => {
    if (maxPower <= ConnectorPowerLevel.LEVEL1)
      return InfinityEnums.ChargingSpeedLevels.LEVEL1
    else if (maxPower <= ConnectorPowerLevel.LEVEL2)
      return InfinityEnums.ChargingSpeedLevels.LEVEL2
    else return InfinityEnums.ChargingSpeedLevels.LEVEL3
  }

  const checkFilterEmpty = (newFilter) => {
    const emptyFlag = !newFilter.some((filter) => filter.value.length !== 0)
    if (emptyFlag) setEmpty(true)
    else setEmpty(false)
  }

  useEffect(() => {
    checkFilterEmpty(cardTransactionsFilter)
  }, [cardTransactionsFilter])

  useEffect(() => {
    if (selectedCard)
      fetchChargingTokenTransactions(
        cardTransactionsFilter,
        paginationOffset,
        selectedCard._id,
        ascending,
      )
  }, [selectedCard])
  const options = {
    scrollbars: {
      visibility: 'auto',
      autoHide: 'move',
      autoHideDelay: 1000,
    },
  }
  const rootRef = useRef(null)
  const [scroller, setScroller] = useState(null)
  const [initialize, osInstance] = useOverlayScrollbars({
    options,
    defer: true,
  })

  useEffect(() => {
    const { current: root } = rootRef
    if (scroller && root) {
      initialize({
        target: root,
        elements: {
          viewport: scroller,
        },
      })
    }
    return () => osInstance()?.destroy()
  }, [scroller, initialize, osInstance])

  return (
    <div className={Styles.container}>
      {(fetchChargingTokenTransactionsRequestState === RequestState.LOADING ||
        fetchCardRequestState === RequestState.LOADING ||
        fetchChargingTokensRequestState === RequestState.LOADING) &&
      paginationOffset === 0 ? (
        [...Array(15)].map((e, index) => (
          <TransactionListItemSkeleton key={index} />
        ))
      ) : chargingTokenTransactions.length === 0 ? (
        empty ? (
          <EmptyLogs />
        ) : (
          <NoLogsFilter />
        )
      ) : (
        <div
          ref={rootRef}
          data-overlayscrollbars=""
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <Virtuoso
            data={chargingTokenTransactions}
            scrollerRef={setScroller}
            endReached={
              paginationOffset &&
              chargingTokenTransactions &&
              chargingTokenTransactions.length !== 0
                ? loadMoreData
                : () => {}
            }
            increaseViewportBy={480}
            itemContent={(index, chargingTokenTransaction) => {
              return (
                <div key={index}>
                  <TransactionListItem
                    stationName={
                      chargingTokenTransaction.chargePoint.station.name
                    }
                    chargePointName={chargingTokenTransaction.chargePoint.name}
                    chargePointId={chargingTokenTransaction.chargePoint?._id}
                    stationId={chargingTokenTransaction.chargePoint.station._id}
                    energyConsumption={
                      chargingTokenTransaction.energyConsumption
                    }
                    amount={
                      chargingTokenTransaction.amount?.inclVat
                        ? chargingTokenTransaction.amount.inclVat
                        : chargingTokenTransaction.amount.exclVat
                    }
                    processedAt={chargingTokenTransaction.processedAt}
                    endedAt={chargingTokenTransaction.endedAt}
                    startedAt={chargingTokenTransaction.startedAt}
                    connectorUid={chargingTokenTransaction.connector?.uid}
                    connectorType={chargingTokenTransaction.connector?.type}
                    tariff={chargingTokenTransaction.tariff}
                    chargingLevel={handleMaxPower(
                      chargingTokenTransaction?.connector?.maxPower,
                    )}
                    status={chargingTokenTransaction?.credit}
                    currency={
                      chargingTokenTransaction.operator?.country?.currency
                    }
                    costBreakdown={chargingTokenTransaction.costBreakdown}
                  />
                </div>
              )
            }}
            components={{ Footer: Footer }}
          />
        </div>
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchChargingTokenTransactions: (
      filter,
      offset,
      chargingTokenId,
      ascending,
    ) =>
      dispatch(
        CardActions.fetchCardTransactions(
          filter,
          offset,
          chargingTokenId,
          ascending,
        ),
      ),
  }
}

const mapStateToProps = (state) => ({
  chargingTokenTransactions: CardSelectors.getCardTransactions(state),
  fetchChargingTokenTransactionsRequestState:
    CardSelectors.getFetchCardTransactionsRequestState(state),
  cardTransactionsFilter: CardSelectors.getCardTransactionsFilter(state),
  paginationOffset: CardSelectors.getCardTransactionsPaginationOffset(state),
  chargingTokens: CardSelectors.getCards(state),
  selectedChargingTokenIndex: CardSelectors.getSelectedIndex(state),
  fetchChargingTokensRequestState:
    CardSelectors.getFetchCardListRequestState(state),
  selectedCard: CardSelectors.getSelectedCard(state),
  fetchCardRequestState: CardSelectors.getFetchCardRequestState(state),
})

ChargingTokenTransactionList.propTypes = {
  fetchChargingTokenTransactions: PropTypes.func,
  chargingTokenTransactions: PropTypes.arrayOf(PropTypes.object),
  fetchChargingTokenTransactionsRequestState: PropTypes.number,
  paginationOffset: PropTypes.number,
  fetchUserTransactions: PropTypes.func,
  cardTransactionsFilter: PropTypes.string,
  chargingTokens: PropTypes.array,
  selectedChargingTokenIndex: PropTypes.number,
  fetchChargingTokensRequestState: PropTypes.number,
  ascending: PropTypes.bool,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChargingTokenTransactionList)
